import axios from 'axios'
import { SegmentListType } from 'features/Programs/Shared/types'
import Request from 'redux/config/services/Request'
import { Segment, Worksite } from 'redux/employer/employerTypes'
import { PAY_FREQUENCY_LABELS } from './constants'

export type PayFrequencyPayload =
  | 'MONTHLY'
  | 'SEMI_MONTHLY'
  | 'BI_WEEKLY'
  | 'WEEKLY'

export type PayDayPayload =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'

export interface CreatePayCadencePayload {
  name?: string
  pay_frequency: PayFrequencyPayload
  pay_day?: PayDayPayload | null
  pay_date?: string | null
  another_pay_date?: string | null
  is_last_day_of_month?: boolean
  deduction_split?: 50 | 100
  worksites: string[]
}

export interface PayCadenceResponse {
  id: string
  name?: string
  payFrequency: PayFrequencyPayload
  payDay?: PayDayPayload | null
  payDate?: string | null
  anotherPayDate?: string | null
  isLastDayOfMonth?: boolean
  worksites?: string[]
  deductionSplit?: 50 | 100
  status?: string
}

export interface PayCadenceFormState {
  payCadenceName: string
  payCadenceFrequency: string | null
  payCadenceDay: string | null
  payCadenceDate: Date | null
  payCadenceDate2: Date | null
  isLastDayOfMonth: boolean
  worksiteIds: string[]
  splitDeduction: boolean
}

export interface PayCadenceOverviewResponse {
  active: PayCadenceResponse[]
  current: PayCadenceResponse | null
  upcoming: PayCadenceResponse | null
}

const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const parseApiDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) {
    return null
  }
  const [year, month, day] = dateStr.split('-').map(Number)
  if (!year || !month || !day) {
    return null
  }
  return new Date(year, month - 1, day)
}

const frequencyToUiId: Record<PayFrequencyPayload, string> = {
  MONTHLY: 'monthly',
  SEMI_MONTHLY: 'semi-monthly',
  BI_WEEKLY: 'bi-weekly',
  WEEKLY: 'weekly',
}

const dayToUiId: Record<PayDayPayload, string> = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday',
}

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

export const formatPayFrequencyLabel = (
  payFrequency: PayFrequencyPayload | string | null | undefined
) => {
  if (!payFrequency) {
    return '—'
  }
  return PAY_FREQUENCY_LABELS[payFrequency] ?? payFrequency
}

export const formatPayScheduleSummary = (payCadence: PayCadenceResponse) => {
  const frequency = payCadence.payFrequency
  if (frequency === 'MONTHLY') {
    if (payCadence.isLastDayOfMonth) {
      return 'Last day of each month'
    }
    if (payCadence.payDate) {
      const date = parseApiDate(payCadence.payDate)
      return date ? `Day ${date.getDate()} of each month` : '—'
    }
    return '—'
  }
  if (frequency === 'SEMI_MONTHLY') {
    const first = parseApiDate(payCadence.payDate)
    if (payCadence.isLastDayOfMonth && first) {
      return `Day ${first.getDate()} and last day of each month`
    }
    const second = parseApiDate(payCadence.anotherPayDate)
    if (first && second) {
      return `Days ${first.getDate()} and ${second.getDate()}`
    }
    return '—'
  }
  if (frequency === 'BI_WEEKLY') {
    const date = parseApiDate(payCadence.payDate)
    const day = payCadence.payDay ? capitalize(payCadence.payDay) : null
    if (day && date) {
      return `${day}, starting ${date.toLocaleDateString()}`
    }
    return day ?? '—'
  }
  if (frequency === 'WEEKLY' && payCadence.payDay) {
    return `Every ${capitalize(payCadence.payDay)}`
  }
  return '—'
}

export const formStateFromPayCadence = (
  payCadence: PayCadenceResponse
): PayCadenceFormState | null => {
  if (!payCadence.payFrequency) {
    return null
  }

  const payCadenceFrequency = frequencyToUiId[payCadence.payFrequency] ?? null
  if (!payCadenceFrequency) {
    return null
  }

  return {
    payCadenceName: payCadence.name ?? '',
    payCadenceFrequency,
    payCadenceDay: payCadence.payDay
      ? dayToUiId[payCadence.payDay] ?? null
      : null,
    payCadenceDate: parseApiDate(payCadence.payDate),
    payCadenceDate2: parseApiDate(payCadence.anotherPayDate),
    isLastDayOfMonth: Boolean(payCadence.isLastDayOfMonth),
    worksiteIds: payCadence.worksites ?? [],
    splitDeduction: payCadence.deductionSplit === 50,
  }
}

const lastDayOfMonth = (reference: Date) =>
  new Date(reference.getFullYear(), reference.getMonth() + 1, 0)

export const worksiteIdsToSegments = (
  worksiteIds: string[],
  worksites: Worksite[],
  allSegments: Segment[],
  groupWorksites: Record<string, string[]>,
  segmentItems: SegmentListType[]
): SegmentListType[] => {
  const worksiteIdSet = new Set(worksiteIds)
  const selected: SegmentListType[] = []

  segmentItems.forEach((item) => {
    if (item.type === 'WORKSITE') {
      const worksite = worksites.find((ws) => ws.segmentId === item.id)
      if (worksite && worksiteIdSet.has(worksite.id)) {
        selected.push(item)
      }
      return
    }

    if (item.type === 'CUSTOM') {
      const memberWorksites = groupWorksites[item.id] || []
      if (
        memberWorksites.length > 0 &&
        memberWorksites.every((worksiteId) => worksiteIdSet.has(worksiteId))
      ) {
        selected.push(item)
      }
    }
  })

  return selected
}

export const formatGroupsLabel = (
  worksiteIds: string[],
  worksites: Worksite[],
  allSegments: Segment[],
  groupWorksites: Record<string, string[]>,
  segmentItems: SegmentListType[]
) => {
  const labels = worksiteIdsToSegments(
    worksiteIds,
    worksites,
    allSegments,
    groupWorksites,
    segmentItems
  ).map((segment) => segment.text)

  return labels.length > 0 ? labels.join(', ') : '—'
}

export const getAssignedWorksiteIds = (
  cadences: PayCadenceResponse[],
  excludeCadenceId?: string | null
) => {
  const assigned = new Set<string>()
  cadences.forEach((cadence) => {
    if (excludeCadenceId && cadence.id === excludeCadenceId) {
      return
    }
    cadence.worksites?.forEach((worksiteId) => assigned.add(worksiteId))
  })
  return assigned
}

export const filterAvailableSegmentItems = (
  segmentItems: SegmentListType[],
  assignedWorksiteIds: Set<string>,
  worksites: Worksite[],
  allSegments: Segment[],
  groupWorksites: Record<string, string[]>
) => {
  return segmentItems.filter((item) => {
    const worksiteIds = segmentsToWorksiteIds(
      [item],
      worksites,
      allSegments,
      groupWorksites
    )
    if (worksiteIds.length === 0) {
      return true
    }
    return worksiteIds.every((id) => !assignedWorksiteIds.has(id))
  })
}

export const getPayCadenceApiErrorMessage = (
  error: unknown,
  fallback = 'Unable to save pay schedule. Please try again.'
): string => {
  if (!axios.isAxiosError(error) || !error.response?.data) {
    return fallback
  }

  const data = error.response.data
  if (typeof data === 'string') {
    return data
  }

  if (typeof data === 'object' && data !== null) {
    const messages: string[] = []
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        messages.push(...value.map(String))
      } else if (typeof value === 'string') {
        messages.push(value)
      }
    }
    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return fallback
}

export const segmentsToWorksiteIds = (
  selectedSegments: SegmentListType[],
  worksites: Worksite[],
  allSegments: Segment[],
  groupWorksites: Record<string, string[]>
): string[] => {
  const worksiteIds = new Set<string>()

  selectedSegments.forEach((selected) => {
    if (selected.type === 'WORKSITE') {
      const worksite = worksites.find((ws) => ws.segmentId === selected.id)
      if (worksite) {
        worksiteIds.add(worksite.id)
      }
      return
    }

    if (selected.type === 'CUSTOM') {
      ;(groupWorksites[selected.id] || []).forEach((worksiteId) => {
        worksiteIds.add(worksiteId)
      })
    }
  })

  return Array.from(worksiteIds)
}

export const toApiPayCadencePayload = (cadence: {
  payCadenceName?: string
  payCadenceFrequency: string | null
  payCadenceDay: string | null
  payCadenceDate: Date | null
  payCadenceDate2: Date | null
  isLastDayOfMonth?: boolean
  worksiteIds: string[]
  splitDeduction: boolean
}): CreatePayCadencePayload | null => {
  if (cadence.worksiteIds.length === 0) {
    return null
  }
  const frequencyMap: Record<string, PayFrequencyPayload> = {
    monthly: 'MONTHLY',
    'semi-monthly': 'SEMI_MONTHLY',
    'bi-weekly': 'BI_WEEKLY',
    weekly: 'WEEKLY',
  }
  const dayMap: Record<string, PayDayPayload> = {
    monday: 'MONDAY',
    tuesday: 'TUESDAY',
    wednesday: 'WEDNESDAY',
    thursday: 'THURSDAY',
    friday: 'FRIDAY',
    saturday: 'SATURDAY',
    sunday: 'SUNDAY',
  }

  const pay_frequency = cadence.payCadenceFrequency
    ? frequencyMap[cadence.payCadenceFrequency]
    : null
  if (!pay_frequency) {
    return null
  }

  const payload: CreatePayCadencePayload = {
    pay_frequency,
    worksites: cadence.worksiteIds,
    deduction_split: cadence.splitDeduction ? 50 : 100,
  }

  if (cadence.payCadenceName?.trim()) {
    payload.name = cadence.payCadenceName.trim()
  }

  if (pay_frequency === 'MONTHLY') {
    if (cadence.isLastDayOfMonth) {
      payload.is_last_day_of_month = true
      const reference = cadence.payCadenceDate ?? new Date(2020, 0, 1)
      payload.pay_date = formatDate(reference)
    } else {
      if (!cadence.payCadenceDate) {
        return null
      }
      payload.pay_date = formatDate(cadence.payCadenceDate)
      payload.is_last_day_of_month = false
    }
  } else if (pay_frequency === 'SEMI_MONTHLY') {
    if (!cadence.payCadenceDate) {
      return null
    }
    payload.pay_date = formatDate(cadence.payCadenceDate)
    if (cadence.isLastDayOfMonth) {
      payload.is_last_day_of_month = true
      payload.another_pay_date = formatDate(lastDayOfMonth(cadence.payCadenceDate))
    } else {
      if (!cadence.payCadenceDate2) {
        return null
      }
      payload.another_pay_date = formatDate(cadence.payCadenceDate2)
      payload.is_last_day_of_month = false
    }
  } else if (pay_frequency === 'BI_WEEKLY') {
    if (!cadence.payCadenceDay || !cadence.payCadenceDate) {
      return null
    }
    payload.pay_day = dayMap[cadence.payCadenceDay]
    payload.pay_date = formatDate(cadence.payCadenceDate)
  } else if (pay_frequency === 'WEEKLY') {
    if (!cadence.payCadenceDay) {
      return null
    }
    payload.pay_day = dayMap[cadence.payCadenceDay]
  }

  return payload
}

const getGroupWorksites = (segmentIds: string[]) => {
  return Request.post('/hr/deductions/pay-cadences/group-worksites/', {
    segment_ids: segmentIds,
  })
}

const listActivePayCadences = () => {
  return Request.get('/hr/deductions/pay-cadences/?status=ACTIVE')
}

const getPayCadenceOverview = () => {
  return Request.get('/hr/deductions/pay-cadences/overview/')
}

const createPayCadence = (payload: CreatePayCadencePayload) => {
  return Request.post('/hr/deductions/pay-cadences/', payload)
}

const updatePayCadence = (id: string, payload: CreatePayCadencePayload) => {
  return Request.patch(`/hr/deductions/pay-cadences/${id}/`, payload)
}

const deletePayCadence = (id: string) => {
  return Request.del(`/hr/deductions/pay-cadences/${id}/`)
}

export default {
  listActivePayCadences,
  getPayCadenceOverview,
  createPayCadence,
  updatePayCadence,
  deletePayCadence,
  getGroupWorksites,
  toApiPayCadencePayload,
  formStateFromPayCadence,
  worksiteIdsToSegments,
  segmentsToWorksiteIds,
  formatGroupsLabel,
  formatPayFrequencyLabel,
  formatPayScheduleSummary,
  getAssignedWorksiteIds,
  filterAvailableSegmentItems,
  getPayCadenceApiErrorMessage,
}
